import { QueryClient } from "@tanstack/react-query";

import { ItemUpdaterProps } from "./types/update-item-props";
import { defaultGetItemId } from "./utils/default-get-item-id";

/**
 * Factory function that creates an updater for a single item in the React Query cache.
 * Works for both string ids (default) and numeric ids (HCC API, `TId = number`).
 */
export const createItemUpdaterForQuery = <
  TItem = { id: string },
  TUpdate extends Partial<TItem> = Partial<TItem>,
  TId extends string | number = string
>(
  queryClient: QueryClient,
  { getQueryKey, getItemId: customGetItemId }: ItemUpdaterProps<TItem, TUpdate, TId>
) => {
  const getItemId = (customGetItemId ?? defaultGetItemId) as (
    item: TItem | TUpdate
  ) => TId | undefined;

  return async (beforeId: TId, updatedItem: TUpdate | null) => {
    const id = (updatedItem && getItemId(updatedItem)) ?? beforeId;

    // Item was moved
    if (beforeId !== id) {
      queryClient.removeQueries({ queryKey: getQueryKey(beforeId) });
    }

    const allQueries = queryClient.getQueryCache().findAll({
      queryKey: getQueryKey(id),
      exact: false,
    });

    allQueries.forEach(({ queryKey }) => {
      const oldQuery = queryClient.getQueryData<TItem>(queryKey);

      if (!oldQuery) return;

      if (!updatedItem) return queryClient.removeQueries({ queryKey });

      queryClient.setQueryData<TItem>(queryKey, { ...oldQuery, ...updatedItem });
    });
  };
};

// Alias for backward compatibility during transition
export const updateItemInQuery = createItemUpdaterForQuery;
